/**
 * A component to represent a single directory node
 *
 * @author Aakash Dhawan (adhawan@yext.com)
 */
import {classNames} from '/js-spruce/yext/spruce/tools/classNames2';
import {TreeActionsContainer} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/actions/TreeActionsContainer';
import {TreeNodeShape} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/shapesJsDoc';
import {SelectMode} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/SelectMode';

/**
 * @param {{
 *   node: !TreeNodeShape,
 *   onHoverRow: !function(string),
 *   isRootNode: boolean,
 *   isExpanded: boolean,
 *   treeSelectMode: string,
 *   treeActionsMode: (string | undefined),
 *   children: (React.ReactNode|undefined),
 *   rowRef: {current: {getBoundingClientRect: !Function}},
 *   onExpand: ?function(),
 *   onBulkSelect: ?function(),
 *   onDoubleClick: ?function(string),
 * }} props
 * @return {React.ReactNode}
 */
export const DirectoryTreeNode = React.memo(props => {
  const {
    node,
    onHoverRow,
    isRootNode,
    isExpanded,
    treeSelectMode,
    treeActionsMode,
    children,
    rowRef,
    onExpand,
    onBulkSelect,
    onDoubleClick,
  } = props;

  const [isHovered, setIsHovered] = React.useState(false);

  const handleOnHoverRow = () => {
    setIsHovered(true);
    onHoverRow(node.id);
  };

  const treeGroupClassNames = classNames({
    'tree-node__directory__group': true,
    'tree-node__directory__root': isRootNode,
    'tree-node__hovered': isHovered,
  });

  const iconClassName = classNames({
    'tree-node__directory__icon': true,
    'icon-arrow-down': !isRootNode && isExpanded,
    'icon-arrow-right': !isRootNode && !isExpanded,
  });

  const displayNameClassName = classNames({
    'tree-node__directory__name': true,
    'tree-node__error': node.hasError,
  });

  const childrenClassName = classNames({
    'tree-node__directory__children': !isRootNode,
  });

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={'tree-node__directory'}
      onClick={e => {
        e.stopPropagation();
        onExpand && onExpand();
      }}>
      {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
      <div
        className={treeGroupClassNames}
        onMouseOver={handleOnHoverRow}
        onMouseLeave={() => setIsHovered(false)}
      >
        <i className={iconClassName}/>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/mouse-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <i
          className="tree-node__directory__icon"
          onClick={e => e.stopPropagation()}
          onDoubleClick={onDoubleClick}
        />
        <SelectMode
          node={node}
          treeSelectMode={treeSelectMode}
          onBulkSelect={onBulkSelect}
        />
        {(isHovered && treeActionsMode)
        && <TreeActionsContainer mode={treeActionsMode} rowRef={rowRef}/>}
      </div>
      <div className={childrenClassName}>
        {(isRootNode || isExpanded) && children}
      </div>
    </div>
  );
});
